import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { inventoryAPI, locationsAPI, filterUserLocations } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogBody,
  DialogFooter,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
} from '../components/ui/dropdown-menu';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { 
  Plus, Package, AlertTriangle, ArrowUpDown, Filter, 
  Clock, History, Pill, Stethoscope, FileBox, Briefcase,
  Calendar, TrendingDown, TrendingUp, ScanLine, QrCode, X,
  FileText, RefreshCw, Upload, ImageOff, Layers, Image as ImageIcon,
  Download, Search, ArrowRightLeft, MoreHorizontal, Edit, Trash2,
  BarChart3, ArrowUp, ArrowDown, ChevronDown, ChevronRight,
  ChevronLeft, ChevronsLeft, ChevronsRight, FileSpreadsheet, Barcode
} from 'lucide-react';
import BarcodeScanner from '../components/ui/BarcodeScanner';
import QRCodeDisplay from '../components/ui/QRCodeDisplay';
import DataPagination from '../components/DataPagination';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  { value: 'medication', label: 'Medication', icon: Pill },
  { value: 'medical_supply', label: 'Medical Supply', icon: Stethoscope },
  { value: 'medical_equipment', label: 'Medical Equipment', icon: FileBox },
  { value: 'office_supply', label: 'Office Supply', icon: Briefcase },
];

const SUBCATEGORIES = {
  medication: [
    { value: 'injectables', label: 'Injectables' },
    { value: 'other', label: 'Other' },
  ],
};

const UNIT_MEASUREMENTS = [
  { group: 'Count-Based', units: [
    { value: 'each', label: 'Each' },
    { value: 'unit', label: 'Unit' },
    { value: 'piece', label: 'Piece' },
    { value: 'tablet', label: 'Tablet' },
    { value: 'pill', label: 'Pill' },
    { value: 'capsule', label: 'Capsule' },
    { value: 'vial', label: 'Vial' },
    { value: 'ampule', label: 'Ampule' },
    { value: 'syringe', label: 'Syringe' },
    { value: 'needle', label: 'Needle' },
    { value: 'test', label: 'Test' },
    { value: 'strip', label: 'Strip' },
  ]},
  { group: 'Packaging', units: [
    { value: 'box', label: 'Box' },
    { value: 'pack', label: 'Pack' },
    { value: 'case', label: 'Case' },
    { value: 'carton', label: 'Carton' },
    { value: 'bundle', label: 'Bundle' },
    { value: 'tray', label: 'Tray' },
    { value: 'roll', label: 'Roll' },
  ]},
  { group: 'Volume', units: [
    { value: 'mL', label: 'mL (milliliter)' },
    { value: 'L', label: 'L (liter)' },
    { value: 'fl_oz', label: 'fl oz (fluid ounce)' },
    { value: 'cup', label: 'Cup' },
  ]},
  { group: 'Weight/Mass', units: [
    { value: 'mcg', label: 'mcg (microgram)' },
    { value: 'mg', label: 'mg (milligram)' },
    { value: 'g', label: 'g (gram)' },
    { value: 'kg', label: 'kg (kilogram)' },
    { value: 'oz', label: 'oz (ounce)' },
    { value: 'lb', label: 'lb (pound)' },
  ]},
  { group: 'Length', units: [
    { value: 'mm', label: 'mm (millimeter)' },
    { value: 'cm', label: 'cm (centimeter)' },
    { value: 'm', label: 'm (meter)' },
    { value: 'in', label: 'in (inch)' },
    { value: 'ft', label: 'ft (foot)' },
  ]},
  { group: 'Area', units: [
    { value: 'cm2', label: 'cm² (square cm)' },
    { value: 'in2', label: 'in² (square inch)' },
    { value: 'm2', label: 'm² (square meter)' },
  ]},
  { group: 'Time-Based', units: [
    { value: 'minute', label: 'Minute' },
    { value: 'hour', label: 'Hour' },
    { value: 'day', label: 'Day' },
    { value: 'week', label: 'Week' },
  ]},
  { group: 'Dose-Based', units: [
    { value: 'dose', label: 'Dose' },
    { value: 'injection', label: 'Injection' },
    { value: 'IU', label: 'IU (International Unit)' },
    { value: 'unit_dose', label: 'Unit Dose' },
  ]},
];

const ACTION_TYPE_LABELS = {
  add: { label: 'Added', color: 'text-emerald-600', icon: TrendingUp },
  consume: { label: 'Consumed', color: 'text-amber-600', icon: TrendingDown },
  adjust: { label: 'Adjusted', color: 'text-blue-600', icon: ArrowUpDown },
  expire: { label: 'Expired', color: 'text-rose-600', icon: Clock },
  reverse: { label: 'Reversed', color: 'text-purple-600', icon: History },
  transfer_out: { label: 'Transferred Out', color: 'text-orange-600', icon: ArrowRightLeft },
  transfer_in: { label: 'Transferred In', color: 'text-teal-600', icon: ArrowRightLeft },
};

const Inventory = () => {
  const navigate = useNavigate();
  const { user, isClinicAdmin, isInventoryManager, workingLocation } = useAuth();
  const canManageInventory = isClinicAdmin() || isInventoryManager();
  const [inventory, setInventory] = useState([]);
  const [locations, setLocations] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [expirationAlerts, setExpirationAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [adjustDialogOpen, setAdjustDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [subcategoryFilter, setSubcategoryFilter] = useState('all');
  const [showLowStock, setShowLowStock] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('inventory');
  const [inventorySortBy, setInventorySortBy] = useState('name');
  const [inventorySortOrder, setInventorySortOrder] = useState('asc');
  const [logViewType, setLogViewType] = useState('all'); // 'all', 'quantity', 'field'
  const [txnSearchQuery, setTxnSearchQuery] = useState(''); // Search for transaction log
  const [exportingExcel, setExportingExcel] = useState(false);
  
  // Pagination state for inventory
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  
  // Pagination state for transactions
  const [txnPage, setTxnPage] = useState(1);
  const [txnPageSize, setTxnPageSize] = useState(20);
  const [txnTotalItems, setTxnTotalItems] = useState(0);
  const [txnTotalPages, setTxnTotalPages] = useState(0);
  
  // Pagination state for audit logs
  const [auditPage, setAuditPage] = useState(1);
  const [auditPageSize, setAuditPageSize] = useState(20);
  const [auditTotalItems, setAuditTotalItems] = useState(0);
  const [auditTotalPages, setAuditTotalPages] = useState(0);
  
  // Barcode scanner state
  const [scannerOpen, setScannerOpen] = useState(false);
  const [scanAction, setScanAction] = useState('lookup'); // 'lookup', 'add', 'consume'
  const [barcodeDialogOpen, setBarcodeDialogOpen] = useState(false);
  const [selectedItemForBarcode, setSelectedItemForBarcode] = useState(null);
  const [scannedItem, setScannedItem] = useState(null);
  const [quickActionDialogOpen, setQuickActionDialogOpen] = useState(false);
  
  // Image upload state
  const [imageDialogOpen, setImageDialogOpen] = useState(false);
  const [selectedItemForImage, setSelectedItemForImage] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);

  // Delete confirmation state
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);
  
  // Image lightbox state
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImage, setLightboxImage] = useState(null);
  const [lightboxTitle, setLightboxTitle] = useState('');
  
  const [formData, setFormData] = useState({
    name: '',
    sku: '',
    category: 'medication',
    subcategory: '',
    unit_of_measure: 'unit',
    unit_price: '',  // Sell price
    reorder_threshold: '10',
    manufacturer: '',
    expiration_date: '',
    storage_requirement: '',
    prescription_required: false,
    image_url: '',
    description: '',
  });

  const [adjustForm, setAdjustForm] = useState({
    item_id: '',
    quantity_change: '',
    reason: '',
    action_type: 'adjust' // 'add' or 'adjust'
  });

  // Transfer dialog state
  const [transferDialogOpen, setTransferDialogOpen] = useState(false);
  const [transferForm, setTransferForm] = useState({
    item_id: '',
    item_name: '',
    from_location_id: '',
    from_location_name: '',
    to_location_id: '',
    quantity: '',
    available_quantity: 0,
    reason: ''
  });
  const [transferLoading, setTransferLoading] = useState(false);

  // (Usage Report moved to Reports page)

  // (Usage Report fetch/sort/expand functions moved to Reports page)

  // Debounce search query (400ms)
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchQuery), 400);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Fetch locations once on mount (they rarely change)
  useEffect(() => {
    const fetchLocations = async () => {
      try {
        const locationsRes = await locationsAPI.getAll({ page_size: 100 });
        const locData = locationsRes.data.items || locationsRes.data;
        const allLocations = Array.isArray(locData) ? locData : [];
        setLocations(filterUserLocations(allLocations, user));
      } catch (error) {
        console.error('Failed to fetch locations:', error);
      }
    };
    fetchLocations();
  }, [user, workingLocation]);

  useEffect(() => {
    fetchData();
  }, [categoryFilter, subcategoryFilter, showLowStock, currentPage, pageSize, debouncedSearch, workingLocation, inventorySortBy, inventorySortOrder]);

  useEffect(() => {
    if (activeTab === 'transactions' && canManageInventory) {
      fetchTransactions();
      fetchAuditLogs();
    } else if (activeTab === 'alerts' && canManageInventory) {
      fetchExpirationAlerts();
    }
  }, [activeTab, canManageInventory]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const params = { page: currentPage, page_size: pageSize, sort_by: inventorySortBy, sort_order: inventorySortOrder };
      if (categoryFilter !== 'all') params.category = categoryFilter;
      if (subcategoryFilter !== 'all') params.subcategory = subcategoryFilter;
      if (showLowStock) params.low_stock = true;
      if (debouncedSearch) params.search = debouncedSearch;
      if (workingLocation?.id) params.location_id = workingLocation.id;
      
      const inventoryRes = await inventoryAPI.getAll(params);
      
      // Handle paginated responses
      const invResponse = inventoryRes.data;
      if (invResponse.items) {
        setInventory(invResponse.items);
        setTotalItems(invResponse.total || 0);
        setTotalPages(invResponse.total_pages || 0);
      } else {
        setInventory(Array.isArray(invResponse) ? invResponse : []);
        setTotalItems(0);
        setTotalPages(0);
      }
    } catch (error) {
      toast.error('Failed to fetch inventory');
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleInventorySort = (field) => {
    if (inventorySortBy === field) {
      setInventorySortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setInventorySortBy(field);
      setInventorySortOrder('asc');
    }
    setCurrentPage(1);
  };

  const handlePageSizeChange = (size) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  const fetchTransactions = async (page = txnPage, size = txnPageSize, search = txnSearchQuery) => {
    try {
      const params = { page, page_size: size };
      if (workingLocation?.id) params.location_id = workingLocation.id;
      if (search) params.search = search; // Search by item name, SKU, or bill ID
      const transactionsRes = await inventoryAPI.getTransactions(params);
      // Handle paginated response
      const transData = transactionsRes.data.items || transactionsRes.data;
      setTransactions(Array.isArray(transData) ? transData : []);
      setTxnTotalItems(transactionsRes.data.total || 0);
      setTxnTotalPages(transactionsRes.data.total_pages || 1);
    } catch (error) {
      console.error('Failed to fetch transactions:', error);
    }
  };

  const fetchAuditLogs = async (page = auditPage, size = auditPageSize) => {
    try {
      const params = { page, page_size: size };
      if (workingLocation?.id) params.location_id = workingLocation.id;
      const auditLogsRes = await inventoryAPI.getAuditLogs(params);
      // Handle paginated response
      const auditData = auditLogsRes.data.items || auditLogsRes.data;
      setAuditLogs(Array.isArray(auditData) ? auditData : []);
      setAuditTotalItems(auditLogsRes.data.total || 0);
      setAuditTotalPages(auditLogsRes.data.total_pages || 1);
    } catch (error) {
      console.error('Failed to fetch audit logs:', error);
    }
  };

  const handleTxnPageChange = (page) => {
    setTxnPage(page);
    fetchTransactions(page, txnPageSize);
  };

  const handleTxnPageSizeChange = (size) => {
    setTxnPageSize(size);
    setTxnPage(1);
    fetchTransactions(1, size);
  };

  const handleAuditPageChange = (page) => {
    setAuditPage(page);
    fetchAuditLogs(page, auditPageSize);
  };

  const handleAuditPageSizeChange = (size) => {
    setAuditPageSize(size);
    setAuditPage(1);
    fetchAuditLogs(1, size);
  };

  // Export inventory to Excel
  const exportInventoryToExcel = async () => {
    setExportingExcel(true);
    try {
      // Build query params for filtered inventory
      const params = { page: 1, page_size: 500 }; // Max 500 per API limit
      if (categoryFilter !== 'all') params.category = categoryFilter;
      if (subcategoryFilter !== 'all') params.subcategory = subcategoryFilter;
      if (showLowStock) params.low_stock = true;
      if (debouncedSearch) params.search = debouncedSearch;
      if (workingLocation?.id) params.location_id = workingLocation.id;
      
      const response = await inventoryAPI.getAll(params);
      const items = response.data.items || response.data || [];
      
      if (items.length === 0) {
        toast.error('No inventory items to export');
        return;
      }
      
      // Find location names
      const locationMap = {};
      locations.forEach(loc => { locationMap[loc.id] = loc.name; });
      
      // Fetch batches for all items
      const batchPromises = items.map(item => 
        inventoryAPI.getBatches(item.id, true).then(res => ({ itemId: item.id, batches: res.data.batches || res.data || [] })).catch(() => ({ itemId: item.id, batches: [] }))
      );
      const batchResults = await Promise.all(batchPromises);
      const batchMap = {};
      batchResults.forEach(r => { batchMap[r.itemId] = r.batches; });
      
      // Build CSV with item rows + batch sub-rows
      const headers = ['Item', 'Category', 'Location', 'Quantity', 'Sell Price', 'Avg Cost', 'Lot Number', 'Qty Purchased', 'Qty Remaining', 'Purchase Cost', 'Purchase Date', 'Expiration Date', 'Invoice URL', 'Depleted', 'Created At'];
      const rows = [];
      
      for (const item of items) {
        const batches = batchMap[item.id] || [];
        const catLabel = CATEGORIES.find(c => c.value === item.category)?.label || item.category || '';
        const locName = locationMap[item.location_id] || 'Unknown';
        
        // Item row
        rows.push([
          item.name || '',
          catLabel,
          locName,
          item.quantity_on_hand || 0,
          item.unit_price?.toFixed(2) || item.selling_price?.toFixed(2) || '0.00',
          item.average_cost > 0 ? item.average_cost.toFixed(2) : '0.00',
          '', '', '', '', '', '', '', '', ''
        ]);
        
        // Batch rows
        for (const batch of batches) {
          const invoiceUrls = (batch.invoice_image_urls && batch.invoice_image_urls.length > 0) 
            ? batch.invoice_image_urls.join(' | ') 
            : (batch.invoice_image_url || '');
          rows.push([
            '', '', '', '', '', '',
            batch.lot_number || '',
            batch.quantity_purchased || 0,
            batch.quantity_remaining || 0,
            batch.purchase_cost?.toFixed?.(2) || batch.purchase_cost || '0.00',
            batch.purchase_date || '',
            batch.expiration_date || '',
            invoiceUrls,
            batch.is_depleted ? 'Yes' : 'No',
            batch.created_at || ''
          ]);
        }
      }
      
      const csvContent = [
        headers.join(','),
        ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
      ].join('\n');
      
      const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const dateStr = new Date().toISOString().split('T')[0];
      const locationName = workingLocation?.name?.replace(/\s+/g, '_') || 'All';
      link.download = `inventory_${locationName}_${dateStr}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      toast.success(`Exported ${items.length} items to Excel`);
    } catch (error) {
      console.error('Failed to export inventory:', error);
      toast.error('Failed to export inventory');
    } finally {
      setExportingExcel(false);
    }
  };

  const fetchExpirationAlerts = async () => {
    try {
      const response = await inventoryAPI.getExpirationAlerts(90, workingLocation?.id);
      setExpirationAlerts(response.data);
    } catch (error) {
      console.error('Failed to fetch expiration alerts:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        name: formData.name,
        sku: formData.sku,
        category: formData.category,
        subcategory: formData.subcategory || null,
        unit_of_measure: formData.unit_of_measure,
        unit_price: parseFloat(formData.unit_price),
        reorder_threshold: parseInt(formData.reorder_threshold),
        manufacturer: formData.manufacturer || null,
        expiration_date: formData.expiration_date || null,
        storage_requirement: formData.storage_requirement || null,
        prescription_required: formData.prescription_required,
        image_url: formData.image_url || null,
        description: formData.description || null,
      };
      
      if (editingItem) {
        await inventoryAPI.update(editingItem.id, data);
        toast.success('Item updated successfully');
      } else {
        // No initial quantity - stock is added via batches only
        data.initial_quantity = 0;
        await inventoryAPI.create(data);
        toast.success('Item added successfully', { 
          description: 'Add stock using the "Add Stock" button' 
        });
      }
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save item');
    }
  };

  const handleAdjust = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        item_id: adjustForm.item_id,
        quantity_change: parseInt(adjustForm.quantity_change),
        reason: adjustForm.reason
      };
      
      if (adjustForm.action_type === 'add') {
        await inventoryAPI.add(payload);
        toast.success('Inventory added');
      } else {
        await inventoryAPI.adjust(payload);
        toast.success('Inventory adjusted');
      }
      
      setAdjustDialogOpen(false);
      setAdjustForm({ item_id: '', quantity_change: '', reason: '', action_type: 'adjust' });
      fetchData();
      if (activeTab === 'transactions') {
        fetchTransactions();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to adjust inventory');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      sku: '',
      category: 'medication',
      subcategory: '',
      unit_of_measure: 'unit',
      unit_price: '',
      reorder_threshold: '10',
      manufacturer: '',
      expiration_date: '',
      storage_requirement: '',
      prescription_required: false,
      image_url: '',
      description: '',
    });
    setEditingItem(null);
  };

  const handleDeleteItem = async () => {
    if (!editingItem) return;
    setDeleting(true);
    try {
      await inventoryAPI.delete(editingItem.id);
      toast.success('Item permanently deleted', {
        description: `${editingItem.name} and all its batches have been removed`
      });
      setDeleteConfirmOpen(false);
      setDeleteConfirmText('');
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete item');
    } finally {
      setDeleting(false);
    }
  };

  // Transfer functions
  const openTransferDialog = (item) => {
    setTransferForm({
      item_id: item.id,
      item_name: item.name,
      from_location_id: item.location_id,
      from_location_name: item.location_name || locations.find(l => l.id === item.location_id)?.name || 'Unknown',
      to_location_id: '',
      quantity: '',
      available_quantity: item.quantity_on_hand || item.quantity || 0,
      reason: ''
    });
    setTransferDialogOpen(true);
  };

  const handleTransfer = async (e) => {
    e.preventDefault();
    
    if (!transferForm.to_location_id) {
      toast.error('Please select a destination location');
      return;
    }
    
    if (!transferForm.quantity || parseInt(transferForm.quantity) <= 0) {
      toast.error('Please enter a valid quantity');
      return;
    }
    
    if (parseInt(transferForm.quantity) > transferForm.available_quantity) {
      toast.error(`Cannot transfer more than available quantity (${transferForm.available_quantity})`);
      return;
    }
    
    setTransferLoading(true);
    try {
      const response = await inventoryAPI.transfer({
        item_id: transferForm.item_id,
        from_location_id: transferForm.from_location_id,
        to_location_id: transferForm.to_location_id,
        quantity: parseInt(transferForm.quantity),
        reason: transferForm.reason || undefined
      });
      
      toast.success(response.data.message || 'Transfer completed successfully');
      setTransferDialogOpen(false);
      setTransferForm({
        item_id: '',
        item_name: '',
        from_location_id: '',
        from_location_name: '',
        to_location_id: '',
        quantity: '',
        available_quantity: 0,
        reason: ''
      });
      fetchData();
      if (activeTab === 'transactions') {
        fetchTransactions();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to transfer inventory');
    } finally {
      setTransferLoading(false);
    }
  };

  const openEditDialog = (item) => {
    setEditingItem(item);
    setFormData({
      name: item.name,
      sku: item.sku,
      category: item.category || 'medication',
      subcategory: item.subcategory || '',
      unit_of_measure: item.unit_of_measure || 'unit',
      unit_price: item.unit_price.toString(),
      reorder_threshold: (item.reorder_threshold || item.min_quantity || 10).toString(),
      manufacturer: item.manufacturer || '',
      expiration_date: item.expiration_date ? item.expiration_date.split('T')[0] : '',
      storage_requirement: item.storage_requirement || '',
      prescription_required: item.prescription_required || false,
      image_url: item.image_url || '',
      description: item.description || '',
    });
    setDialogOpen(true);
  };

  const openAdjustDialog = (item, actionType = 'adjust') => {
    setAdjustForm({
      item_id: item.id,
      quantity_change: '',
      reason: '',
      action_type: actionType
    });
    setAdjustDialogOpen(true);
  };

  // Barcode scanning functions
  const handleBarcodeScan = (code) => {
    setScannerOpen(false);
    
    // Search for item by SKU or barcode
    const item = inventory.find(i => 
      i.sku?.toLowerCase() === code.toLowerCase() ||
      i.sku?.replace(/-/g, '') === code.replace(/-/g, '')
    );
    
    if (item) {
      setScannedItem(item);
      if (scanAction === 'lookup') {
        // Show item details with quick actions
        setQuickActionDialogOpen(true);
      } else if (scanAction === 'add' && canManageInventory) {
        openAdjustDialog(item, 'add');
      } else if (scanAction === 'consume') {
        openAdjustDialog(item, 'consume');
      }
    } else {
      toast.error(`Item not found: ${code}`, {
        description: 'No inventory item matches this barcode/SKU'
      });
    }
  };

  const openScannerFor = (action) => {
    setScanAction(action);
    setScannerOpen(true);
  };

  const openBarcodeDialog = (item) => {
    setSelectedItemForBarcode(item);
    setBarcodeDialogOpen(true);
  };

  // Image lightbox functions
  const openLightbox = (imageUrl, title) => {
    setLightboxImage(imageUrl);
    setLightboxTitle(title);
    setLightboxOpen(true);
  };

  // Image upload functions
  const openImageDialog = (item) => {
    setSelectedItemForImage(item);
    setPreviewImage(item.image_url || null);
    setDragActive(false);
    setImageDialogOpen(true);
  };

  const validateAndPreviewFile = (file) => {
    if (!file) return false;
    
    // Validate file type
    if (!['image/jpeg', 'image/png', 'image/webp', 'image/gif'].includes(file.type)) {
      toast.error('Invalid file type', { description: 'Please select a JPEG, PNG, WebP, or GIF image' });
      return false;
    }
    
    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File too large', { description: 'Maximum file size is 5MB' });
      return false;
    }
    
    // Show preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreviewImage(reader.result);
    };
    reader.readAsDataURL(file);
    return true;
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    validateAndPreviewFile(file);
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      if (validateAndPreviewFile(file)) {
        // Create a DataTransfer to set the file input
        const dataTransfer = new DataTransfer();
        dataTransfer.items.add(file);
        if (fileInputRef.current) {
          fileInputRef.current.files = dataTransfer.files;
        }
      }
    }
  };

  const uploadImage = async () => {
    const file = fileInputRef.current?.files[0];
    if (!file || !selectedItemForImage) {
      toast.error('Please select an image file');
      return;
    }
    
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(
        `${API_URL}/api/inventory/${selectedItemForImage.id}/upload-image`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          }
        }
      );
      
      toast.success('Image uploaded successfully', {
        description: `${selectedItemForImage.name} image has been updated`
      });
      setImageDialogOpen(false);
      setPreviewImage(null);
      setDragActive(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
      fetchData(); // Refresh inventory list
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const getLocationName = (locationId) => {
    const location = locations.find(l => l.id === locationId);
    return location?.name || 'Unknown';
  };

  const getCategoryIcon = (category) => {
    const cat = CATEGORIES.find(c => c.value === category);
    return cat?.icon || Package;
  };

  const getCategoryLabel = (category, subcategory) => {
    const cat = CATEGORIES.find(c => c.value === category);
    const label = cat?.label || category;
    if (subcategory) {
      const sub = SUBCATEGORIES[category]?.find(s => s.value === subcategory);
      return `${label} — ${sub?.label || subcategory}`;
    }
    return label;
  };

  const getUnitLabel = (unitValue) => {
    for (const group of UNIT_MEASUREMENTS) {
      const unit = group.units.find(u => u.value === unitValue);
      if (unit) return unit.label;
    }
    return unitValue || 'unit';
  };

  const getQuantity = (item) => item.quantity_on_hand ?? item.quantity ?? 0;
  const getThreshold = (item) => item.reorder_threshold ?? item.min_quantity ?? 10;
  const isLowStock = (item) => getQuantity(item) <= getThreshold(item);
  const isExpired = (item) => item.is_expired || false;

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    // Handle date-only strings (YYYY-MM-DD) to avoid timezone shifting
    if (dateStr.length === 10 && dateStr.includes('-')) {
      const [year, month, day] = dateStr.split('-');
      return new Date(year, month - 1, day).toLocaleDateString();
    }
    return new Date(dateStr).toLocaleDateString();
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString();
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="inventory-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Inventory</h1>
          <p className="text-slate-500 text-sm mt-1">Manage medications and supplies</p>
        </div>
        
        <div className="flex items-center gap-2">
          {/* Barcode Scanner Button - Only for inventory managers, not clinic admins */}
          {!isClinicAdmin() && (
            <Button 
              variant="outline" 
              onClick={() => openScannerFor('lookup')}
              className="border-slate-200"
              data-testid="scan-barcode-btn"
            >
              <ScanLine className="w-4 h-4 mr-2" />
              Scan Barcode
            </Button>
          )}
          
          {canManageInventory && (
            <Button
              variant="outline"
              className="border-emerald-200 text-emerald-700 hover:bg-emerald-50"
              onClick={() => navigate('/inventory/batch-import')}
              data-testid="batch-import-btn"
            >
              <FileSpreadsheet className="w-4 h-4 mr-2" />
              Import Batch
            </Button>
          )}
          
          {canManageInventory && (
            <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
              <DialogTrigger asChild>
                <Button className="bg-gradient-to-r from-blue-700 to-indigo-700 hover:from-blue-800 hover:to-indigo-800 text-white shadow-md" data-testid="add-inventory-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Item
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>{editingItem ? 'Edit Item' : 'Add Inventory Item'}</DialogTitle>
                  <DialogDescription>
                    {editingItem ? 'Update item details' : 'Add a new medication or supply to inventory'}
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleSubmit}>
                  <DialogBody className="space-y-4">
                    {/* Current Quantity Section - Only shown when editing */}
                    {editingItem && (
                      <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                        <div className="flex items-center justify-between">
                          <div>
                            <Label className="text-slate-700 font-medium">Current Quantity</Label>
                            <div className="flex items-center gap-2 mt-1">
                              <span className={`text-2xl font-bold ${isLowStock(editingItem) ? 'text-amber-600' : 'text-slate-900'}`}>
                                {getQuantity(editingItem)}
                              </span>
                              <span className="text-slate-500">{editingItem.unit_of_measure || 'units'}</span>
                              {isLowStock(editingItem) && (
                                <span className="flex items-center gap-1 text-amber-600 text-sm">
                                  <AlertTriangle className="w-4 h-4" />
                                  Low Stock
                                </span>
                              )}
                            </div>
                            <p className="text-xs text-slate-400 mt-1">Reorder threshold: {getThreshold(editingItem)}</p>
                          </div>
                          <div className="flex gap-2">
                            <Button
                              type="button"
                              size="sm"
                              className="bg-emerald-600 hover:bg-emerald-700 text-white"
                              onClick={() => {
                                setDialogOpen(false);
                                openAdjustDialog(editingItem, 'add');
                              }}
                            >
                              <TrendingUp className="w-4 h-4 mr-1" />
                              Add Stock
                            </Button>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setDialogOpen(false);
                                openAdjustDialog(editingItem, 'adjust');
                              }}
                            >
                              <ArrowUpDown className="w-4 h-4 mr-1" />
                              Adjust
                            </Button>
                          </div>
                        </div>
                      </div>
                    )}

                    <div className="grid grid-cols-2 gap-4">
                      <div className="col-span-2">
                        <Label htmlFor="name" className="text-slate-700">Item Name *</Label>
                        <Input
                          id="name"
                          placeholder="e.g., Ibuprofen 200mg"
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          required
                          className="mt-1.5"
                          data-testid="inventory-name-input"
                        />
                    </div>
                    <div>
                      <Label htmlFor="category" className="text-slate-700">Category *</Label>
                      <Select 
                        value={formData.category} 
                        onValueChange={(val) => setFormData({ ...formData, category: val, subcategory: '' })}
                      >
                        <SelectTrigger className="mt-1.5" data-testid="inventory-category-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {CATEGORIES.map((cat) => (
                            <SelectItem key={cat.value} value={cat.value}>
                              <div className="flex items-center gap-2">
                                <cat.icon className="w-4 h-4" />
                                {cat.label}
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    {SUBCATEGORIES[formData.category] && (
                    <div>
                      <Label className="text-slate-700">Subcategory</Label>
                      <Select 
                        value={formData.subcategory} 
                        onValueChange={(val) => setFormData({ ...formData, subcategory: val })}
                      >
                        <SelectTrigger className="mt-1.5" data-testid="inventory-subcategory-select">
                          <SelectValue placeholder="Select subcategory" />
                        </SelectTrigger>
                        <SelectContent>
                          {SUBCATEGORIES[formData.category].map((sub) => (
                            <SelectItem key={sub.value} value={sub.value}>{sub.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    )}
                    <div>
                      <Label htmlFor="unit_of_measure" className="text-slate-700">Unit of Measure</Label>
                      <Select 
                        value={formData.unit_of_measure} 
                        onValueChange={(val) => setFormData({ ...formData, unit_of_measure: val })}
                      >
                        <SelectTrigger className="mt-1.5" data-testid="inventory-unit-select">
                          <SelectValue placeholder="Select unit" />
                        </SelectTrigger>
                        <SelectContent className="max-h-[300px]">
                          {UNIT_MEASUREMENTS.map((group) => (
                            <div key={group.group}>
                              <div className="px-2 py-1.5 text-xs font-semibold text-slate-500 bg-slate-50">
                                {group.group}
                              </div>
                              {group.units.map((unit) => (
                                <SelectItem key={unit.value} value={unit.value}>
                                  {unit.label}
                                </SelectItem>
                              ))}
                            </div>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="unit_price" className="text-slate-700">Sell Price ($) *</Label>
                      <Input
                        id="unit_price"
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder="0.00"
                        value={formData.unit_price}
                        onChange={(e) => setFormData({ ...formData, unit_price: e.target.value })}
                        required
                        className="mt-1.5"
                        data-testid="inventory-price-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="reorder_threshold" className="text-slate-700">Reorder Threshold</Label>
                      <Input
                        id="reorder_threshold"
                        type="number"
                        min="0"
                        placeholder="10"
                        value={formData.reorder_threshold}
                        onChange={(e) => setFormData({ ...formData, reorder_threshold: e.target.value })}
                        className="mt-1.5"
                        data-testid="inventory-threshold-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="manufacturer" className="text-slate-700">Manufacturer</Label>
                      <Input
                        id="manufacturer"
                        placeholder="e.g., Pfizer"
                        value={formData.manufacturer}
                        onChange={(e) => setFormData({ ...formData, manufacturer: e.target.value })}
                        className="mt-1.5"
                      />
                    </div>
                    <div>
                      <Label htmlFor="storage_requirement" className="text-slate-700">Storage Requirement</Label>
                      <Input
                        id="storage_requirement"
                        placeholder="e.g., Refrigerate 2-8°C"
                        value={formData.storage_requirement}
                        onChange={(e) => setFormData({ ...formData, storage_requirement: e.target.value })}
                        className="mt-1.5"
                      />
                    </div>
                    <div className="col-span-2">
                      <Label htmlFor="description" className="text-slate-700">Description</Label>
                      <Textarea
                        id="description"
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        placeholder="Brief description of the item"
                        className="mt-1.5"
                        rows={2}
                      />
                    </div>
                    <div className="col-span-2 flex items-center gap-4">
                      <div className="flex items-center gap-2">
                        <Switch
                          id="prescription_required"
                          checked={formData.prescription_required}
                          onCheckedChange={(checked) => setFormData({ ...formData, prescription_required: checked })}
                        />
                        <Label htmlFor="prescription_required" className="text-slate-700">Prescription Required</Label>
                      </div>
                    </div>
                  </div>
                </DialogBody>
                <DialogFooter className={editingItem && isClinicAdmin() ? 'flex justify-between sm:justify-between' : ''}>
                  {editingItem && isClinicAdmin() && (
                    <Button
                      type="button"
                      variant="outline"
                      className="border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700"
                      onClick={() => { setDeleteConfirmOpen(true); setDeleteConfirmText(''); }}
                      data-testid="delete-inventory-btn"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Delete Item
                    </Button>
                  )}
                  <div className="flex gap-2">
                    <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="border-slate-200">
                      Cancel
                    </Button>
                    <Button type="submit" className="bg-gradient-to-r from-blue-700 to-indigo-700 hover:from-blue-800 hover:to-indigo-800 text-white shadow-md" data-testid="save-inventory-btn">
                      {editingItem ? 'Update Item' : 'Add Item'}
                    </Button>
                  </div>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        )}

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteConfirmOpen} onOpenChange={(open) => { setDeleteConfirmOpen(open); if (!open) setDeleteConfirmText(''); }}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="text-red-600 flex items-center gap-2">
                <Trash2 className="w-5 h-5" />
                Delete Inventory Item
              </DialogTitle>
              <DialogDescription>
                This will permanently delete <span className="font-semibold text-slate-900">{editingItem?.name}</span> and all its batches, transactions, and audit logs. This action cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <DialogBody className="space-y-4">
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-sm text-red-700">
                  Type <span className="font-mono font-bold bg-red-100 px-1.5 py-0.5 rounded">DELETE</span> to confirm permanent deletion.
                </p>
              </div>
              <Input
                placeholder="Type DELETE to confirm"
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                className="font-mono"
                data-testid="delete-confirm-input"
              />
            </DialogBody>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => { setDeleteConfirmOpen(false); setDeleteConfirmText(''); }} className="border-slate-200">
                Cancel
              </Button>
              <Button
                type="button"
                disabled={deleteConfirmText !== 'DELETE' || deleting}
                className="bg-red-600 hover:bg-red-700 text-white disabled:opacity-50"
                onClick={handleDeleteItem}
                data-testid="delete-confirm-btn"
              >
                {deleting ? 'Deleting...' : 'Permanently Delete'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        </div>
      </div>

      {/* Tabs for Admin */}
      {canManageInventory ? (
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-blue-50 border border-blue-100">
            <TabsTrigger value="inventory" className="data-[state=active]:bg-white data-[state=active]:text-blue-700">
              <Package className="w-4 h-4 mr-2" />
              Inventory
            </TabsTrigger>
            <TabsTrigger value="transactions" className="data-[state=active]:bg-white data-[state=active]:text-blue-700">
              <History className="w-4 h-4 mr-2" />
              Transaction Log
            </TabsTrigger>
            <TabsTrigger value="alerts" className="data-[state=active]:bg-white data-[state=active]:text-blue-700">
              <AlertTriangle className="w-4 h-4 mr-2" />
              Alerts
              {expirationAlerts.length > 0 && (
                <span className="ml-2 bg-amber-500 text-white text-xs rounded-full px-2 py-0.5">
                  {expirationAlerts.length}
                </span>
              )}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="inventory" className="mt-4">
            {/* Filters */}
            <div className="flex items-center gap-4 mb-4 flex-wrap">
              {/* Search Input */}
              <div className="relative">
                <ScanLine className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Search by name or SKU..."
                  value={searchQuery}
                  onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1); }}
                  className="pl-9 w-56 bg-white border-slate-200"
                  data-testid="inventory-search"
                />
              </div>
              
              <Filter className="w-4 h-4 text-slate-400" />
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" className="w-52 justify-between bg-white border-slate-200" data-testid="category-filter">
                    <span className="truncate">
                      {categoryFilter === 'all' 
                        ? 'All Categories' 
                        : getCategoryLabel(categoryFilter, subcategoryFilter !== 'all' ? subcategoryFilter : null)}
                    </span>
                    <ChevronDown className="w-4 h-4 ml-1 shrink-0 opacity-50" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-52">
                  <DropdownMenuItem 
                    onClick={() => { setCategoryFilter('all'); setSubcategoryFilter('all'); setCurrentPage(1); }}
                    className={categoryFilter === 'all' ? 'bg-slate-100 font-medium' : ''}
                  >
                    All Categories
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  {CATEGORIES.map((cat) => (
                    SUBCATEGORIES[cat.value] ? (
                      <DropdownMenuSub key={cat.value}>
                        <DropdownMenuSubTrigger className={categoryFilter === cat.value ? 'bg-slate-100 font-medium' : ''}>
                          <cat.icon className="w-4 h-4 mr-2" />
                          {cat.label}
                        </DropdownMenuSubTrigger>
                        <DropdownMenuSubContent>
                          <DropdownMenuItem
                            onClick={() => { setCategoryFilter(cat.value); setSubcategoryFilter('all'); setCurrentPage(1); }}
                            className={categoryFilter === cat.value && subcategoryFilter === 'all' ? 'bg-slate-100 font-medium' : ''}
                          >
                            All {cat.label}
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          {SUBCATEGORIES[cat.value].map((sub) => (
                            <DropdownMenuItem
                              key={sub.value}
                              onClick={() => { setCategoryFilter(cat.value); setSubcategoryFilter(sub.value); setCurrentPage(1); }}
                              className={categoryFilter === cat.value && subcategoryFilter === sub.value ? 'bg-slate-100 font-medium' : ''}
                            >
                              {sub.label}
                            </DropdownMenuItem>
                          ))}
                        </DropdownMenuSubContent>
                      </DropdownMenuSub>
                    ) : (
                      <DropdownMenuItem
                        key={cat.value}
                        onClick={() => { setCategoryFilter(cat.value); setSubcategoryFilter('all'); setCurrentPage(1); }}
                        className={categoryFilter === cat.value ? 'bg-slate-100 font-medium' : ''}
                      >
                        <cat.icon className="w-4 h-4 mr-2" />
                        {cat.label}
                      </DropdownMenuItem>
                    )
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
              
              <div className="flex items-center gap-2">
                <Switch
                  id="low-stock"
                  checked={showLowStock}
                  onCheckedChange={(val) => { setShowLowStock(val); setCurrentPage(1); }}
                />
                <Label htmlFor="low-stock" className="text-sm flex items-center gap-1 text-slate-600">
                  <AlertTriangle className="w-4 h-4 text-amber-500" />
                  Low Stock Only
                </Label>
              </div>
              
              {/* Export Button */}
              <Button
                variant="outline"
                size="sm"
                onClick={exportInventoryToExcel}
                disabled={exportingExcel || inventory.length === 0}
                className="ml-auto"
                data-testid="export-inventory-btn"
              >
                <Download className="w-4 h-4 mr-1" />
                {exportingExcel ? 'Exporting...' : 'Export Excel'}
              </Button>
            </div>

            {/* Inventory Table */}
            <Card className="border-slate-200">
              <CardContent className="p-0">
                {loading ? (
                  <div className="flex items-center justify-center h-64">
                    <div className="spinner"></div>
                  </div>
                ) : inventory.length === 0 ? (
                  <div className="empty-state py-16">
                    <Package className="empty-state-icon" />
                    <p className="empty-state-title">No inventory items found</p>
                    <p className="empty-state-text">{searchQuery ? 'Try a different search term' : 'Add your first item to get started'}</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-slate-50 hover:bg-slate-50">
                          <TableHead className="text-slate-600 font-medium w-12">Image</TableHead>
                          {[
                            { key: 'name', label: 'Item' },
                            { key: 'category', label: 'Category' },
                          ].map(col => (
                            <TableHead
                              key={col.key}
                              className="text-slate-600 font-medium cursor-pointer hover:bg-slate-100 select-none"
                              onClick={() => handleInventorySort(col.key)}
                              data-testid={`sort-${col.key}`}
                            >
                              <div className="flex items-center gap-1">
                                {col.label}
                                {inventorySortBy === col.key ? (
                                  inventorySortOrder === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
                                ) : (
                                  <ArrowUpDown className="w-3 h-3 text-slate-300" />
                                )}
                              </div>
                            </TableHead>
                          ))}
                          <TableHead className="text-slate-600 font-medium">Location</TableHead>
                          {[
                            { key: 'quantity', label: 'Quantity' },
                          ].map(col => (
                            <TableHead
                              key={col.key}
                              className="text-slate-600 font-medium cursor-pointer hover:bg-slate-100 select-none"
                              onClick={() => handleInventorySort(col.key)}
                              data-testid={`sort-${col.key}`}
                            >
                              <div className="flex items-center gap-1">
                                {col.label}
                                {inventorySortBy === col.key ? (
                                  inventorySortOrder === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
                                ) : (
                                  <ArrowUpDown className="w-3 h-3 text-slate-300" />
                                )}
                              </div>
                            </TableHead>
                          ))}
                          <TableHead className="text-slate-600 font-medium">Sell Price</TableHead>
                          <TableHead className="text-slate-600 font-medium">Avg Cost</TableHead>
                          {[
                            { key: 'expiration', label: 'Expiration' },
                            { key: 'status', label: 'Status' },
                          ].map(col => (
                            <TableHead
                              key={col.key}
                              className="text-slate-600 font-medium cursor-pointer hover:bg-slate-100 select-none"
                              onClick={() => handleInventorySort(col.key)}
                              data-testid={`sort-${col.key}`}
                            >
                              <div className="flex items-center gap-1">
                                {col.label}
                                {inventorySortBy === col.key ? (
                                  inventorySortOrder === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
                                ) : (
                                  <ArrowUpDown className="w-3 h-3 text-slate-300" />
                                )}
                              </div>
                            </TableHead>
                          ))}
                          {canManageInventory && <TableHead className="text-right text-slate-600 font-medium">Actions</TableHead>}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {inventory.map((item) => {
                          const CategoryIcon = getCategoryIcon(item.category);
                          return (
                            <TableRow 
                              key={item.id} 
                              className={`${isLowStock(item) ? 'bg-amber-50 hover:bg-amber-100' : ''} ${isExpired(item) ? 'bg-rose-50 hover:bg-rose-100' : 'hover:bg-slate-50'}`}
                              data-testid={`inventory-row-${item.id}`}
                            >
                              <TableCell className="w-14 p-2">
                                {item.image_url ? (
                                  <div className="relative group">
                                    <button
                                      onClick={() => openLightbox(item.image_url, item.name)}
                                      className="cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500 rounded-lg"
                                      title="Click to view larger"
                                    >
                                      <img 
                                        src={item.image_url} 
                                        alt={item.name}
                                        className="w-12 h-12 object-cover rounded-lg border-2 border-slate-200 group-hover:border-blue-400 transition-all duration-200 group-hover:shadow-md"
                                        onError={(e) => {
                                          e.target.style.display = 'none';
                                          e.target.parentElement.nextSibling?.classList.remove('hidden');
                                        }}
                                      />
                                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 rounded-lg transition-all flex items-center justify-center">
                                        <span className="text-white opacity-0 group-hover:opacity-100 text-xs font-medium">View</span>
                                      </div>
                                    </button>
                                    <div className="w-12 h-12 bg-slate-100 rounded-lg border-2 border-slate-200 items-center justify-center hidden">
                                      <ImageOff className="w-5 h-5 text-slate-400" />
                                    </div>
                                    {canManageInventory && (
                                      <button
                                        onClick={(e) => { e.stopPropagation(); openImageDialog(item); }}
                                        className="absolute -bottom-1 -right-1 p-1 bg-blue-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity shadow-sm hover:bg-blue-600"
                                        title="Change image"
                                      >
                                        <Upload className="w-3 h-3" />
                                      </button>
                                    )}
                                  </div>
                                ) : canManageInventory ? (
                                  <button
                                    onClick={() => openImageDialog(item)}
                                    className="w-12 h-12 bg-slate-50 rounded-lg border-2 border-dashed border-slate-300 flex items-center justify-center hover:border-blue-400 hover:bg-blue-50 transition-all cursor-pointer group"
                                    title="Click to upload image"
                                  >
                                    <Upload className="w-5 h-5 text-slate-400 group-hover:text-blue-500" />
                                  </button>
                                ) : (
                                  <div className="w-12 h-12 bg-slate-100 rounded-lg border-2 border-dashed border-slate-300 flex items-center justify-center">
                                    <ImageOff className="w-5 h-5 text-slate-400" />
                                  </div>
                                )}
                              </TableCell>
                              <TableCell>
                                <div className="flex flex-col">
                                  <div className="flex items-center gap-2">
                                    <span className="font-medium text-slate-900">{item.name}</span>
                                    {item.prescription_required && (
                                      <span className="text-xs bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded">Rx</span>
                                    )}
                                  </div>
                                  <span className="text-xs text-slate-500">SKU: {item.sku}</span>
                                  {item.manufacturer && (
                                    <span className="text-xs text-slate-400">{item.manufacturer}</span>
                                  )}
                                </div>
                              </TableCell>
                              <TableCell>
                                <div className="flex items-center gap-2 text-slate-600">
                                  <CategoryIcon className="w-4 h-4" />
                                  <span className="text-sm">{getCategoryLabel(item.category, item.subcategory)}</span>
                                </div>
                              </TableCell>
                              <TableCell className="text-slate-600">{getLocationName(item.location_id)}</TableCell>
                              <TableCell>
                                <div className="flex items-center gap-2">
                                  <span className={isLowStock(item) ? 'text-amber-600 font-medium' : 'text-slate-900'}>
                                    {getQuantity(item)}
                                  </span>
                                  <span className="text-slate-400 text-xs">{item.unit_of_measure}</span>
                                  {isLowStock(item) && <AlertTriangle className="w-4 h-4 text-amber-500" />}
                                </div>
                                <span className="text-slate-400 text-xs">Min: {getThreshold(item)}</span>
                              </TableCell>
                              <TableCell className="font-mono text-slate-900">${item.unit_price.toFixed(2)}</TableCell>
                              <TableCell className="font-mono text-slate-600">
                                {item.average_cost > 0 ? `$${item.average_cost.toFixed(2)}` : '-'}
                              </TableCell>
                              <TableCell>
                                {item.expiration_date ? (
                                  <div className={`text-sm ${isExpired(item) ? 'text-rose-600 font-medium' : 'text-slate-600'}`}>
                                    {formatDate(item.expiration_date)}
                                    {isExpired(item) && <span className="block text-xs">EXPIRED</span>}
                                  </div>
                                ) : (
                                  <span className="text-slate-400">-</span>
                                )}
                              </TableCell>
                              <TableCell>
                                {isExpired(item) ? (
                                  <span className="badge-error">Expired</span>
                                ) : isLowStock(item) ? (
                                  <span className="badge-warning">Low Stock</span>
                                ) : item.is_active ? (
                                  <span className="badge-success">Active</span>
                                ) : (
                                  <span className="badge-neutral">Inactive</span>
                                )}
                              </TableCell>
                              {canManageInventory && (
                                <TableCell className="text-right">
                                  <div className="flex items-center justify-end gap-2">
                                    {/* Primary Action - Add Stock */}
                                    <Button 
                                      size="sm" 
                                      onClick={() => navigate(`/inventory/${item.id}/batches?add=true`)}
                                      className="bg-emerald-600 hover:bg-emerald-700 text-white h-8 px-3"
                                      data-testid={`add-stock-${item.id}`}
                                    >
                                      <Plus className="w-3.5 h-3.5 mr-1" />
                                      Stock
                                    </Button>
                                    
                                    {/* More Actions Dropdown */}
                                    <DropdownMenu>
                                      <DropdownMenuTrigger asChild>
                                        <Button 
                                          variant="ghost" 
                                          size="sm"
                                          className="h-8 w-8 p-0 text-slate-500 hover:text-slate-700"
                                          data-testid={`more-actions-${item.id}`}
                                        >
                                          <MoreHorizontal className="w-4 h-4" />
                                        </Button>
                                      </DropdownMenuTrigger>
                                      <DropdownMenuContent align="end" className="w-48">
                                        <DropdownMenuItem 
                                          onClick={() => openAdjustDialog(item, 'adjust')}
                                          className="cursor-pointer"
                                        >
                                          <ArrowUpDown className="w-4 h-4 mr-2 text-blue-600" />
                                          Adjust Quantity
                                        </DropdownMenuItem>
                                        <DropdownMenuItem 
                                          onClick={() => navigate(`/inventory/${item.id}/batches`)}
                                          className="cursor-pointer"
                                        >
                                          <Layers className="w-4 h-4 mr-2 text-purple-600" />
                                          View Batches
                                        </DropdownMenuItem>
                                        <DropdownMenuItem 
                                          onClick={() => openBarcodeDialog(item)}
                                          className="cursor-pointer"
                                        >
                                          <QrCode className="w-4 h-4 mr-2 text-slate-600" />
                                          View QR Code
                                        </DropdownMenuItem>
                                        {isClinicAdmin() && locations.length > 1 && (
                                          <>
                                            <DropdownMenuSeparator />
                                            <DropdownMenuItem 
                                              onClick={() => openTransferDialog(item)}
                                              className="cursor-pointer"
                                            >
                                              <ArrowRightLeft className="w-4 h-4 mr-2 text-orange-600" />
                                              Transfer to Location
                                            </DropdownMenuItem>
                                          </>
                                        )}
                                        <DropdownMenuSeparator />
                                        <DropdownMenuItem 
                                          onClick={() => openEditDialog(item)}
                                          className="cursor-pointer"
                                        >
                                          <Edit className="w-4 h-4 mr-2 text-slate-600" />
                                          Edit Item
                                        </DropdownMenuItem>
                                      </DropdownMenuContent>
                                    </DropdownMenu>
                                  </div>
                                </TableCell>
                              )}
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                )}
                
                {/* Pagination */}
                {!loading && inventory.length > 0 && (
                  <DataPagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    totalItems={totalItems}
                    pageSize={pageSize}
                    onPageChange={handlePageChange}
                    onPageSizeChange={handlePageSizeChange}
                  />
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="transactions" className="mt-4">
            {/* Log Type Filter and Search */}
            <div className="flex items-center gap-4 mb-4 flex-wrap">
              <span className="text-sm text-slate-500">Show:</span>
              <div className="flex gap-2">
                <Button
                  variant={logViewType === 'all' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setLogViewType('all')}
                  className={logViewType === 'all' ? 'bg-blue-100 text-blue-700 hover:bg-blue-200 border-blue-200' : ''}
                >
                  All Changes
                </Button>
                <Button
                  variant={logViewType === 'quantity' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setLogViewType('quantity')}
                  className={logViewType === 'quantity' ? 'bg-blue-100 text-blue-700 hover:bg-blue-200 border-blue-200' : ''}
                >
                  <TrendingUp className="w-3 h-3 mr-1" />
                  Quantity
                </Button>
                <Button
                  variant={logViewType === 'field' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setLogViewType('field')}
                  className={logViewType === 'field' ? 'bg-blue-100 text-blue-700 hover:bg-blue-200 border-blue-200' : ''}
                >
                  <FileText className="w-3 h-3 mr-1" />
                  Field Changes
                </Button>
              </div>
              
              {/* Transaction Search */}
              <div className="relative ml-auto">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Search by item, SKU, or bill ID..."
                  value={txnSearchQuery}
                  onChange={(e) => {
                    setTxnSearchQuery(e.target.value);
                    setTxnPage(1);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      fetchTransactions(1, txnPageSize, txnSearchQuery);
                    }
                  }}
                  className="pl-9 w-64 bg-white border-slate-200"
                  data-testid="transaction-search"
                />
                {txnSearchQuery && (
                  <button
                    onClick={() => {
                      setTxnSearchQuery('');
                      setTxnPage(1);
                      fetchTransactions(1, txnPageSize, '');
                    }}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={() => fetchTransactions(1, txnPageSize, txnSearchQuery)}
                className="shrink-0"
              >
                <Search className="w-4 h-4" />
              </Button>
            </div>

            <Card className="border-slate-200">
              <CardContent className="p-0">
                {/* Quantity Transactions Table */}
                {(logViewType === 'all' || logViewType === 'quantity') && transactions.length > 0 && (
                  <div className="overflow-x-auto">
                    {logViewType === 'all' && (
                      <div className="px-4 py-2 bg-slate-100 border-b flex items-center gap-2">
                        <TrendingUp className="w-4 h-4 text-slate-600" />
                        <span className="text-sm font-medium text-slate-700">Quantity Changes</span>
                      </div>
                    )}
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-slate-50 hover:bg-slate-50">
                          <TableHead className="text-slate-600 font-medium">Date/Time</TableHead>
                          <TableHead className="text-slate-600 font-medium">Item</TableHead>
                          <TableHead className="text-slate-600 font-medium">Action</TableHead>
                          <TableHead className="text-slate-600 font-medium">Quantity</TableHead>
                          <TableHead className="text-slate-600 font-medium">Before → After</TableHead>
                          <TableHead className="text-slate-600 font-medium">User</TableHead>
                          <TableHead className="text-slate-600 font-medium">Reason</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {transactions.map((t) => {
                          const actionInfo = ACTION_TYPE_LABELS[t.action_type] || { label: t.action_type, color: 'text-slate-600' };
                          const ActionIcon = actionInfo.icon || ArrowUpDown;
                          return (
                            <TableRow key={t.id} className="hover:bg-slate-50">
                              <TableCell className="text-sm text-slate-600">{formatDateTime(t.timestamp)}</TableCell>
                              <TableCell className="font-medium text-slate-900">{t.item_name}</TableCell>
                              <TableCell>
                                <div className={`flex items-center gap-1 ${actionInfo.color}`}>
                                  <ActionIcon className="w-4 h-4" />
                                  <span className="text-sm font-medium">{actionInfo.label}</span>
                                </div>
                              </TableCell>
                              <TableCell className="font-mono">
                                {t.action_type === 'consume' ? `-${t.quantity}` : `+${t.quantity}`}
                              </TableCell>
                              <TableCell className="text-sm text-slate-600">
                                {t.quantity_before} → {t.quantity_after}
                              </TableCell>
                              <TableCell className="text-sm text-slate-600">{t.performed_by_name}</TableCell>
                              <TableCell className="text-sm text-slate-500 max-w-xs truncate">{t.reason || '-'}</TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                )}

                {/* Quantity Changes Pagination */}
                {(logViewType === 'all' || logViewType === 'quantity') && transactions.length > 0 && (
                  <div className="border-t border-slate-200 px-4 py-3 bg-slate-50">
                    <DataPagination
                      currentPage={txnPage}
                      totalPages={txnTotalPages}
                      totalItems={txnTotalItems}
                      pageSize={txnPageSize}
                      onPageChange={handleTxnPageChange}
                      onPageSizeChange={handleTxnPageSizeChange}
                      pageSizeOptions={[10, 20, 50]}
                    />
                  </div>
                )}

                {/* Field Changes (Audit Logs) Table */}
                {(logViewType === 'all' || logViewType === 'field') && auditLogs.length > 0 && (
                  <div className="overflow-x-auto">
                    {logViewType === 'all' && (
                      <div className="px-4 py-2 bg-slate-100 border-b border-t flex items-center gap-2">
                        <FileText className="w-4 h-4 text-slate-600" />
                        <span className="text-sm font-medium text-slate-700">Field Changes (Audit Log)</span>
                      </div>
                    )}
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-slate-50 hover:bg-slate-50">
                          <TableHead className="text-slate-600 font-medium">Date/Time</TableHead>
                          <TableHead className="text-slate-600 font-medium">Item</TableHead>
                          <TableHead className="text-slate-600 font-medium">Field</TableHead>
                          <TableHead className="text-slate-600 font-medium">Old Value</TableHead>
                          <TableHead className="text-slate-600 font-medium">New Value</TableHead>
                          <TableHead className="text-slate-600 font-medium">User</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {auditLogs.map((log) => {
                          const isRequired = ['expiration_date', 'lot_number'].includes(log.field_name);
                          return (
                            <TableRow key={log.id} className="hover:bg-slate-50">
                              <TableCell className="text-sm text-slate-600">{formatDateTime(log.timestamp)}</TableCell>
                              <TableCell className="font-medium text-slate-900">{log.item_name}</TableCell>
                              <TableCell>
                                <div className="flex items-center gap-1">
                                  <RefreshCw className={`w-4 h-4 ${isRequired ? 'text-amber-600' : 'text-slate-400'}`} />
                                  <span className={`text-sm font-medium capitalize ${isRequired ? 'text-amber-700' : 'text-slate-600'}`}>
                                    {log.field_name.replace(/_/g, ' ')}
                                  </span>
                                  {isRequired && (
                                    <span className="text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">Required</span>
                                  )}
                                </div>
                              </TableCell>
                              <TableCell className="text-sm text-slate-500">
                                <span className="bg-rose-50 text-rose-700 px-2 py-0.5 rounded">
                                  {log.old_value || '(empty)'}
                                </span>
                              </TableCell>
                              <TableCell className="text-sm text-slate-600">
                                <span className="bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded">
                                  {log.new_value || '(empty)'}
                                </span>
                              </TableCell>
                              <TableCell className="text-sm text-slate-600">{log.performed_by_name}</TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                )}

                {/* Audit Logs Pagination */}
                {(logViewType === 'all' || logViewType === 'field') && auditLogs.length > 0 && (
                  <div className="border-t border-slate-200 px-4 py-3 bg-slate-50">
                    <DataPagination
                      currentPage={auditPage}
                      totalPages={auditTotalPages}
                      totalItems={auditTotalItems}
                      pageSize={auditPageSize}
                      onPageChange={handleAuditPageChange}
                      onPageSizeChange={handleAuditPageSizeChange}
                      pageSizeOptions={[10, 20, 50]}
                    />
                  </div>
                )}

                {/* Empty State */}
                {((logViewType === 'all' && transactions.length === 0 && auditLogs.length === 0) ||
                  (logViewType === 'quantity' && transactions.length === 0) ||
                  (logViewType === 'field' && auditLogs.length === 0)) && (
                  <div className="empty-state py-16">
                    <History className="empty-state-icon" />
                    <p className="empty-state-title">No {logViewType === 'field' ? 'field changes' : logViewType === 'quantity' ? 'quantity changes' : 'changes'} yet</p>
                    <p className="empty-state-text">Inventory changes will appear here</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="alerts" className="mt-4">
            <Card className="border-slate-200">
              <CardContent className="p-0">
                {expirationAlerts.length === 0 ? (
                  <div className="empty-state py-16">
                    <Calendar className="empty-state-icon text-emerald-500" />
                    <p className="empty-state-title">No expiration alerts</p>
                    <p className="empty-state-text">All items are within safe expiration dates</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-slate-50 hover:bg-slate-50">
                          <TableHead className="text-slate-600 font-medium">Item</TableHead>
                          <TableHead className="text-slate-600 font-medium">Location</TableHead>
                          <TableHead className="text-slate-600 font-medium">Expiration Date</TableHead>
                          <TableHead className="text-slate-600 font-medium">Days Left</TableHead>
                          <TableHead className="text-slate-600 font-medium">Quantity</TableHead>
                          <TableHead className="text-slate-600 font-medium">Status</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {expirationAlerts.map((alert) => (
                          <TableRow 
                            key={alert.item_id} 
                            className={alert.days_until_expiration <= 30 ? 'bg-rose-50 hover:bg-rose-100' : alert.days_until_expiration <= 60 ? 'bg-amber-50 hover:bg-amber-100' : 'hover:bg-slate-50'}
                          >
                            <TableCell className="font-medium text-slate-900">{alert.item_name}</TableCell>
                            <TableCell className="text-slate-600">{alert.location_name}</TableCell>
                            <TableCell className="text-slate-600">{formatDate(alert.expiration_date)}</TableCell>
                            <TableCell>
                              <span className={`font-medium ${alert.days_until_expiration <= 30 ? 'text-rose-600' : alert.days_until_expiration <= 60 ? 'text-amber-600' : 'text-slate-600'}`}>
                                {alert.days_until_expiration} days
                              </span>
                            </TableCell>
                            <TableCell className="text-slate-900">{alert.quantity_on_hand}</TableCell>
                            <TableCell>
                              {alert.days_until_expiration <= 0 ? (
                                <span className="badge-error">Expired</span>
                              ) : alert.days_until_expiration <= 30 ? (
                                <span className="badge-error">Critical</span>
                              ) : alert.days_until_expiration <= 60 ? (
                                <span className="badge-warning">Warning</span>
                              ) : (
                                <span className="badge-info">Monitor</span>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      ) : (
        /* Non-admin view - just the inventory table */
        <>
          <div className="flex items-center gap-4 mb-4">
            <Filter className="w-4 h-4 text-slate-400" />
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-48 bg-white border-slate-200">
                <SelectValue placeholder="Filter by category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {CATEGORIES.map((cat) => (
                  <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <Card className="border-slate-200">
            <CardContent className="p-0">
              {loading ? (
                <div className="flex items-center justify-center h-64">
                  <div className="spinner"></div>
                </div>
              ) : inventory.length === 0 ? (
                <div className="empty-state py-16">
                  <Package className="empty-state-icon" />
                  <p className="empty-state-title">No inventory items found</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50 hover:bg-slate-50">
                        <TableHead className="text-slate-600 font-medium">Item</TableHead>
                        <TableHead className="text-slate-600 font-medium">Category</TableHead>
                        <TableHead className="text-slate-600 font-medium">Quantity</TableHead>
                        <TableHead className="text-slate-600 font-medium">Unit Price</TableHead>
                        <TableHead className="text-slate-600 font-medium">Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {inventory.map((item) => {
                        const CategoryIcon = getCategoryIcon(item.category);
                        return (
                          <TableRow key={item.id} className="hover:bg-slate-50">
                            <TableCell>
                              <div className="flex flex-col">
                                <span className="font-medium text-slate-900">{item.name}</span>
                                <span className="text-xs text-slate-500">SKU: {item.sku}</span>
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-2 text-slate-600">
                                <CategoryIcon className="w-4 h-4" />
                                <span className="text-sm">{getCategoryLabel(item.category)}</span>
                              </div>
                            </TableCell>
                            <TableCell>
                              <span className={isLowStock(item) ? 'text-amber-600 font-medium' : 'text-slate-900'}>
                                {getQuantity(item)} {item.unit_of_measure}
                              </span>
                            </TableCell>
                            <TableCell className="font-mono text-slate-900">${item.unit_price.toFixed(2)}</TableCell>
                            <TableCell>
                              {isExpired(item) ? (
                                <span className="badge-error">Expired</span>
                              ) : isLowStock(item) ? (
                                <span className="badge-warning">Low Stock</span>
                              ) : (
                                <span className="badge-success">Available</span>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}

      {/* Adjust/Add Dialog */}
      <Dialog open={adjustDialogOpen} onOpenChange={setAdjustDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {adjustForm.action_type === 'add' ? 'Add Inventory' : 'Adjust Inventory'}
            </DialogTitle>
            <DialogDescription>
              {adjustForm.action_type === 'add' 
                ? 'Add stock to this item' 
                : 'Adjust quantity up or down'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAdjust}>
            <DialogBody className="space-y-4">
              <div>
                <Label htmlFor="quantity_change" className="text-slate-700">
                  {adjustForm.action_type === 'add' ? 'Quantity to Add *' : 'Quantity Change *'}
                </Label>
                <Input
                  id="quantity_change"
                  type="number"
                  value={adjustForm.quantity_change}
                  onChange={(e) => setAdjustForm({ ...adjustForm, quantity_change: e.target.value })}
                  placeholder={adjustForm.action_type === 'add' ? 'e.g., 50' : 'e.g., 10 or -5'}
                  required
                  min={adjustForm.action_type === 'add' ? 1 : undefined}
                  className="mt-1.5"
                  data-testid="adjust-quantity-input"
                />
                {adjustForm.action_type === 'adjust' && (
                  <p className="text-xs text-slate-500 mt-1">Use positive numbers to add, negative to remove</p>
                )}
              </div>
              <div>
                <Label htmlFor="reason" className="text-slate-700">Reason *</Label>
                <Input
                  id="reason"
                  value={adjustForm.reason}
                  onChange={(e) => setAdjustForm({ ...adjustForm, reason: e.target.value })}
                  placeholder={adjustForm.action_type === 'add' ? 'e.g., Restocked from supplier' : 'e.g., Damaged, Expired, Count correction'}
                  required
                  className="mt-1.5"
                  data-testid="adjust-reason-input"
                />
              </div>
            </DialogBody>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setAdjustDialogOpen(false)} className="border-slate-200">
                Cancel
              </Button>
              <Button 
                type="submit" 
                className={adjustForm.action_type === 'add' ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : 'bg-gradient-to-r from-blue-700 to-indigo-700 hover:from-blue-800 hover:to-indigo-800 text-white shadow-md'} 
                data-testid="confirm-adjust-btn"
              >
                {adjustForm.action_type === 'add' ? 'Add Stock' : 'Adjust Inventory'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Barcode Scanner */}
      <BarcodeScanner
        isOpen={scannerOpen}
        onScan={handleBarcodeScan}
        onClose={() => setScannerOpen(false)}
      />

      {/* Transfer Inventory Dialog */}
      <Dialog open={transferDialogOpen} onOpenChange={setTransferDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ArrowRightLeft className="w-5 h-5 text-blue-600" />
              Transfer Inventory
            </DialogTitle>
            <DialogDescription>
              Move inventory from one location to another
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleTransfer}>
            <DialogBody className="space-y-4">
              {/* Item Info */}
              <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                <p className="text-sm text-slate-500">Item</p>
                <p className="font-medium text-slate-900">{transferForm.item_name}</p>
                <p className="text-xs text-slate-500 mt-1">Available: {transferForm.available_quantity} units</p>
              </div>

              {/* From Location (read-only) */}
              <div>
                <Label className="text-slate-700">From Location</Label>
                <div className="mt-1.5 p-2.5 bg-slate-100 rounded-md border border-slate-200">
                  <span className="text-slate-700">{transferForm.from_location_name}</span>
                </div>
              </div>

              {/* To Location */}
              <div>
                <Label htmlFor="to_location" className="text-slate-700">To Location *</Label>
                <Select
                  value={transferForm.to_location_id}
                  onValueChange={(value) => setTransferForm({ ...transferForm, to_location_id: value })}
                >
                  <SelectTrigger className="mt-1.5" data-testid="transfer-to-location">
                    <SelectValue placeholder="Select destination location" />
                  </SelectTrigger>
                  <SelectContent>
                    {locations
                      .filter(loc => loc.id !== transferForm.from_location_id)
                      .map(loc => (
                        <SelectItem key={loc.id} value={loc.id}>
                          {loc.name}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Quantity */}
              <div>
                <Label htmlFor="transfer_quantity" className="text-slate-700">Quantity to Transfer *</Label>
                <Input
                  id="transfer_quantity"
                  type="number"
                  value={transferForm.quantity}
                  onChange={(e) => setTransferForm({ ...transferForm, quantity: e.target.value })}
                  placeholder="Enter quantity"
                  required
                  min={1}
                  max={transferForm.available_quantity}
                  className="mt-1.5"
                  data-testid="transfer-quantity-input"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Maximum: {transferForm.available_quantity} units
                </p>
              </div>

              {/* Reason */}
              <div>
                <Label htmlFor="transfer_reason" className="text-slate-700">Reason (Optional)</Label>
                <Input
                  id="transfer_reason"
                  value={transferForm.reason}
                  onChange={(e) => setTransferForm({ ...transferForm, reason: e.target.value })}
                  placeholder="e.g., Restocking branch location"
                  className="mt-1.5"
                  data-testid="transfer-reason-input"
                />
              </div>
            </DialogBody>
            <DialogFooter>
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setTransferDialogOpen(false)} 
                className="border-slate-200"
                disabled={transferLoading}
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                className="bg-blue-600 hover:bg-blue-700 text-white"
                disabled={transferLoading || !transferForm.to_location_id || !transferForm.quantity}
                data-testid="confirm-transfer-btn"
              >
                {transferLoading ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Transferring...
                  </>
                ) : (
                  <>
                    <ArrowRightLeft className="w-4 h-4 mr-2" />
                    Transfer
                  </>
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* QR Code Display Dialog */}
      <Dialog open={barcodeDialogOpen} onOpenChange={setBarcodeDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Item QR Code</DialogTitle>
            <DialogDescription>
              Print or download this QR code for {selectedItemForBarcode?.name}
            </DialogDescription>
          </DialogHeader>
          <DialogBody className="flex flex-col items-center py-6">
            {selectedItemForBarcode && (
              <QRCodeDisplay
                value={selectedItemForBarcode.sku}
                name={selectedItemForBarcode.name}
                showPrint={true}
                size={160}
              />
            )}
          </DialogBody>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBarcodeDialogOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Quick Action Dialog (after barcode scan) */}
      <Dialog open={quickActionDialogOpen} onOpenChange={setQuickActionDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Item Found</DialogTitle>
            <DialogDescription>
              Select an action for this item
            </DialogDescription>
          </DialogHeader>
          {scannedItem && (
            <DialogBody className="py-4">
              <div className="bg-slate-50 rounded-lg p-4 mb-4">
                <div className="flex items-start gap-4">
                  <div className="flex-1">
                    <h3 className="font-semibold text-slate-900">{scannedItem.name}</h3>
                    <p className="text-sm text-slate-500">SKU: {scannedItem.sku}</p>
                    <div className="mt-2 flex items-center gap-4 text-sm">
                      <span className={`font-medium ${isLowStock(scannedItem) ? 'text-amber-600' : 'text-slate-700'}`}>
                        Qty: {getQuantity(scannedItem)} {scannedItem.unit_of_measure}
                      </span>
                      <span className="text-slate-500">
                        ${scannedItem.unit_price?.toFixed(2)}
                      </span>
                    </div>
                    {isLowStock(scannedItem) && (
                      <div className="mt-2 flex items-center gap-1 text-amber-600 text-xs">
                        <AlertTriangle className="w-3 h-3" />
                        Low Stock
                      </div>
                    )}
                    {isExpired(scannedItem) && (
                      <div className="mt-1 flex items-center gap-1 text-rose-600 text-xs">
                        <Clock className="w-3 h-3" />
                        Expired
                      </div>
                    )}
                  </div>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-2">
                {canManageInventory && (
                  <>
                    <Button
                      className="bg-emerald-600 hover:bg-emerald-700 text-white"
                      onClick={() => {
                        setQuickActionDialogOpen(false);
                        openAdjustDialog(scannedItem, 'add');
                      }}
                    >
                      <TrendingUp className="w-4 h-4 mr-2" />
                      Add Stock
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => {
                        setQuickActionDialogOpen(false);
                        openAdjustDialog(scannedItem, 'adjust');
                      }}
                    >
                      <ArrowUpDown className="w-4 h-4 mr-2" />
                      Adjust
                    </Button>
                  </>
                )}
                <Button
                  variant="outline"
                  onClick={() => {
                    setQuickActionDialogOpen(false);
                    openBarcodeDialog(scannedItem);
                  }}
                >
                  <Barcode className="w-4 h-4 mr-2" />
                  View Barcode
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    setQuickActionDialogOpen(false);
                    openEditDialog(scannedItem);
                  }}
                  disabled={!canManageInventory}
                >
                  <Edit className="w-4 h-4 mr-2" />
                  Edit Details
                </Button>
              </div>
            </DialogBody>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setQuickActionDialogOpen(false)}>
              Close
            </Button>
            <Button 
              className="bg-gradient-to-r from-blue-700 to-indigo-700 hover:from-blue-800 hover:to-indigo-800 text-white shadow-md"
              onClick={() => {
                setQuickActionDialogOpen(false);
                setScannerOpen(true);
              }}
            >
              <ScanLine className="w-4 h-4 mr-2" />
              Scan Another
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Image Upload Dialog */}
      <Dialog open={imageDialogOpen} onOpenChange={setImageDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <div className="p-2 bg-blue-100 rounded-lg">
                <ImageIcon className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <span className="text-lg">Upload Product Image</span>
                <p className="text-sm font-normal text-slate-500 mt-0.5">
                  {selectedItemForImage?.name}
                </p>
              </div>
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-5 py-4">
            {/* Drag & Drop Zone */}
            <div
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              className={`
                relative cursor-pointer rounded-xl border-2 border-dashed transition-all duration-200
                ${dragActive 
                  ? 'border-blue-500 bg-blue-50' 
                  : previewImage 
                    ? 'border-slate-200 bg-slate-50' 
                    : 'border-slate-300 bg-slate-50 hover:border-blue-400 hover:bg-blue-50/50'
                }
              `}
            >
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileSelect}
                accept="image/jpeg,image/png,image/webp,image/gif"
                className="hidden"
                data-testid="image-file-input"
              />
              
              {previewImage ? (
                <div className="p-4 flex flex-col items-center">
                  <div className="relative group">
                    <img 
                      src={previewImage} 
                      alt="Preview" 
                      className="max-h-56 max-w-full object-contain rounded-lg shadow-sm"
                    />
                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center">
                      <span className="text-white text-sm font-medium">Click to change</span>
                    </div>
                  </div>
                  <p className="text-sm text-slate-600 mt-3">
                    Click or drag a new image to replace
                  </p>
                </div>
              ) : (
                <div className="py-10 px-6 flex flex-col items-center">
                  <div className={`p-4 rounded-full mb-4 transition-colors ${dragActive ? 'bg-blue-100' : 'bg-slate-100'}`}>
                    <Upload className={`w-8 h-8 ${dragActive ? 'text-blue-600' : 'text-slate-400'}`} />
                  </div>
                  <p className="text-base font-medium text-slate-700 mb-1">
                    {dragActive ? 'Drop your image here' : 'Drag & drop your image here'}
                  </p>
                  <p className="text-sm text-slate-500 mb-3">or click to browse</p>
                  <div className="flex items-center gap-2 text-xs text-slate-400">
                    <span className="px-2 py-1 bg-slate-100 rounded">JPEG</span>
                    <span className="px-2 py-1 bg-slate-100 rounded">PNG</span>
                    <span className="px-2 py-1 bg-slate-100 rounded">WebP</span>
                    <span className="px-2 py-1 bg-slate-100 rounded">GIF</span>
                    <span className="text-slate-300">|</span>
                    <span>Max 5MB</span>
                  </div>
                </div>
              )}
            </div>
            
            {/* Category Info */}
            {selectedItemForImage && (
              <div className="flex items-center gap-3 px-3 py-2 bg-slate-50 rounded-lg text-sm">
                <div className="flex items-center gap-1.5 text-slate-500">
                  <span>Category:</span>
                  <span className="font-medium text-slate-700 capitalize">{selectedItemForImage.category}</span>
                </div>
                <span className="text-slate-300">•</span>
                <div className="flex items-center gap-1.5 text-slate-500">
                  <span>SKU:</span>
                  <span className="font-medium text-slate-700">{selectedItemForImage.sku}</span>
                </div>
              </div>
            )}
          </div>
          
          <DialogFooter className="gap-2 sm:gap-0">
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => {
                setImageDialogOpen(false);
                setPreviewImage(null);
                setDragActive(false);
                if (fileInputRef.current) fileInputRef.current.value = '';
              }}
            >
              Cancel
            </Button>
            <Button 
              onClick={uploadImage}
              disabled={uploading || !fileInputRef.current?.files?.[0]}
              className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white min-w-[140px]"
              data-testid="upload-image-btn"
            >
              {uploading ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Uploading...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4 mr-2" />
                  Upload Image
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Image Lightbox */}
      {lightboxOpen && (
        <div 
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4 animate-in fade-in duration-200"
          onClick={() => setLightboxOpen(false)}
        >
          <button
            onClick={() => setLightboxOpen(false)}
            className="absolute top-4 right-4 p-2 text-white/70 hover:text-white transition-colors rounded-full hover:bg-white/10"
          >
            <X className="w-6 h-6" />
          </button>
          
          <div 
            className="max-w-4xl max-h-[85vh] animate-in zoom-in-95 duration-200"
            onClick={(e) => e.stopPropagation()}
          >
            <img 
              src={lightboxImage} 
              alt={lightboxTitle}
              className="max-w-full max-h-[80vh] object-contain rounded-lg shadow-2xl"
            />
            <p className="text-white text-center mt-4 text-lg font-medium">{lightboxTitle}</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default Inventory;
